import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';
import strings from 'helper/localization/localization';
import { passwordHasMinCharsAndDigits } from 'helper/validation';
import Loading from 'modules/Loading';

import MockAPI from 'helper/mockApi'; // for prop-types
import API from '../../api/api'; // for prop-types

/**
 * Top-level component that renders on the /users/reset route.
 * Includes a form to reset the password.
 * User sees this page when clicking a link in a password reset email.
 */
export default class ResetPassword extends Component {
  constructor(props) {
    super(props);

    this.state = {
      /** possible error message, which is a string if present */
      errorMessage: null,
      /** desired password from first input in form */
      newPassword: '',
      /** string from input requesting that the user repeats their password */
      newPasswordRepeated: '',
      /** true when password has been changed successfully */
      passwordChangeSuccessful: false,
      /** true when waiting on the response from api call to change password */
      waitingOnApi: false,
      /** to check whether the token is found or not */
      statusCode: null,
    };
  }

  /**
   * Handler for the form being submitted.
   * Validates the password and sets the appropriate error message.
   * If no error, sets error message to null and kicks off API call to reset password.
   * @param {object} e - js object from form submit event, needed for preventDefault
   * @returns {undefined}
   */
  onSubmitResetPassword = (e) => {
    e.preventDefault();

    let errorMessage = null;
    const { newPassword, newPasswordRepeated } = this.state;

    if (!passwordHasMinCharsAndDigits(newPassword)) {
      errorMessage = strings.passwordRequirement;
    } else if (newPassword !== newPasswordRepeated) {
      errorMessage = strings.passwordsDontMatch;
    }

    this.setState(() => ({ errorMessage }), () => {
      if (!errorMessage) {
        this.setNewPassword();
      }
    });
  }

  /**
   * Handler for password input change.
   * @param {object} e - js object from input change event
   * @returns {undefined}
   */
  onChangePassword = (e) => {
    const { value: newPassword } = e.currentTarget;
    this.setState(() => ({ newPassword }));
  }

  /**
   * Handler for password confirmation input change.
   * @param {object} e - js object from input change event
   * @returns {undefined}
   */
  onChangePasswordRepeated = (e) => {
    const { value: newPasswordRepeated } = e.currentTarget;
    this.setState(() => ({ newPasswordRepeated }));
  }

  /**
   * API call to the v2 route for resetting the password.
   * @returns {undefined}
   */
  setNewPassword = () => {
    const { api, passwordResetToken } = this.props;

    this.setState(() => ({ waitingOnApi: true }), () => {
      api.changePassword(passwordResetToken, this.state.newPassword, ({ res: { statusCode, body } }) => {
        // 204 No Content is a successful call in this case
        this.setState(() => ({
          passwordChangeSuccessful: statusCode === 204,
          statusCode,
          errorMessage: statusCode !== 204 ? body.message : '',
          waitingOnApi: false,
        }));
      });
    });
  }

  render() {
    if (this.state.passwordChangeSuccessful) {
      return <Redirect to="/users/reset-success" />;
    }

    const {
      errorMessage,
      newPassword,
      newPasswordRepeated,
      waitingOnApi,
    } = this.state;


    const inputClassNames = `form-control ${errorMessage && 'border-danger'}`;
    const helpBlockClassNames = `mt-2 pr-2 pl-2 ${errorMessage ? 'text-danger' : 'text-muted'}`;

    const submitButtonOrLoadingIcon = waitingOnApi ? <Loading /> : (
      <button
        type="submit"
        className="btn btn-outline-primary btn-lg btn-block"
        id="button--reset-password"
      >
        {strings.resetPassword}
      </button>
    );

    return (
      <div className="container">
        <div className="row">
          <div className="col">
            <h1>{strings.resetYourPassword}</h1>
          </div>
        </div>

        <form className="form-default" onSubmit={this.onSubmitResetPassword}>
          <div className="form-group mb-4">
            <label htmlFor="reset-password">{strings.newPassword}</label>
            <input type="password" className={inputClassNames} onChange={this.onChangePassword} id="reset-password" value={newPassword} />
            <p className={helpBlockClassNames}>{errorMessage || strings.passwordRequirement}</p>
          </div>

          <div className="form-group">
            <label htmlFor="reset-password--repeat">{strings.repeatNewPassword}</label>
            <input type="password" className={inputClassNames} onChange={this.onChangePasswordRepeated} id="reset-password--repeat" value={newPasswordRepeated} />
          </div>
          {submitButtonOrLoadingIcon}
        </form>
      </div>
    );
  }
}

ResetPassword.propTypes = {
  /* api object used to set a new password */
  api: PropTypes.oneOfType([
    PropTypes.instanceOf(API),
    PropTypes.instanceOf(MockAPI),
  ]).isRequired,
  /* token needed for the API call to reset the password, found in url */
  passwordResetToken: PropTypes.string.isRequired,
};



// WEBPACK FOOTER //
// ./src/modules/login/ResetPassword.jsx